import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Shield, Users, DollarSign, MapPin, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CoHostRequestModal from './CoHostRequestModal';

const Hero = ({ scrollY, mousePosition }) => {
  const navigate = useNavigate();
  const [isCoHostModalOpen, setIsCoHostModalOpen] = useState(false);

  return (
    <>
      {/* Serene background with subtle nature-inspired gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute w-72 sm:w-[500px] h-72 sm:h-[500px] rounded-full bg-gradient-to-r from-emerald-50/30 to-green-50/20 blur-3xl"
          style={{
            left: `${15 + mousePosition.x * 0.02}%`,
            top: `${5 + mousePosition.y * 0.02}%`,
            transform: `translateY(${scrollY * 0.03}px)`
          }}
        />
        <div 
          className="absolute w-64 sm:w-96 h-64 sm:h-96 rounded-full bg-gradient-to-r from-stone-50/20 to-amber-50/15 blur-3xl"
          style={{
            right: `${10 + mousePosition.x * 0.02}%`,
            bottom: `${15 + mousePosition.y * 0.02}%`
          }}
        />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 pt-12 sm:pt-16 lg:pt-20 pb-16 sm:pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          
          {/* Hero Image - Placeholder for rotating sanctuary images */}
          <div className="mb-10 sm:mb-12 lg:mb-16 -mx-2 sm:mx-0">
            <div className="relative w-full h-64 sm:h-80 lg:h-96 rounded-xl sm:rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl animate-breathe">
              <img 
                src="/sanctuary-hero-placeholder.jpg" 
                alt="Serene off-grid mountain sanctuary surrounded by forest, perfect for digital detox and mental recovery"
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to a gradient placeholder if image doesn't exist
                  e.target.style.display = 'none';
                  e.target.parentElement.style.background = 'linear-gradient(135deg, #065f46 0%, #047857 50%, #059669 100%)';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>

          {/* Hero Typography */}
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light mb-4 sm:mb-6 text-gray-900 leading-tight tracking-tight">
              Share Your Sanctuary
              <br />
              <span className="font-normal text-emerald-700">with Travelers Seeking Recovery</span>
            </h1>
            
            {/* Host-focused Copy */}
            <div className="mb-8 sm:mb-10 lg:mb-12 max-w-3xl lg:max-w-4xl mx-auto px-4">
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 leading-relaxed font-light">
                Turn your off-grid property into a restorative retreat. Connect with travelers seeking digital detox and mental recovery. Set your own rates, manage bookings easily, and make a meaningful impact while earning income.
              </p>
            </div>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center px-4 mb-10 sm:mb-12">
              <button 
                onClick={() => navigate('/signup')}
                className="w-full sm:w-auto group bg-emerald-700 hover:bg-emerald-800 text-white px-8 sm:px-10 lg:px-12 py-4 sm:py-5 rounded-lg text-base sm:text-lg font-medium shadow-lg shadow-emerald-200/50 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-300/50 flex items-center justify-center"
              >
                <span className="flex items-center justify-center">
                  Become a Host
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              
              <button 
                onClick={() => navigate('/login')}
                className="w-full sm:w-auto group bg-white text-gray-700 px-8 sm:px-10 lg:px-12 py-4 sm:py-5 rounded-lg text-base sm:text-lg font-medium border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all duration-300 flex items-center justify-center"
              >
                Sign In
              </button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 lg:gap-12 px-4 mb-8 sm:mb-10">
              <div className="flex items-center gap-2 text-gray-700">
                <DollarSign className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium">Set Your Own Rates</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium">Secure Payments</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Users className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium">Join 500+ Active Hosts</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium">Easy Listing Process</span>
              </div>
            </div>
          </div>

          {/* Co-host Section */}
          <div className="mt-12 sm:mt-16 lg:mt-20">
            <div className="text-center mb-8 sm:mb-10 max-w-7xl mx-auto px-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-gray-900 mb-3 sm:mb-4 leading-tight tracking-tight">
                A co-host can help you get started
              </h2>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-2xl mx-auto leading-relaxed font-light">
                Now you can hire a local co-host to help create your listing, get your space ready, and more.
              </p>
            </div>

            {/* Co-host Cards Grid - Horizontal Slider - Full Width */}
            <div className="w-full overflow-x-auto overflow-y-hidden mb-8 sm:mb-10 scrollbar-hide">
              <div className="flex gap-6 sm:gap-8 min-w-max px-4 sm:px-6 lg:px-8">
              {/* Co-host 1 */}
              <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-8 sm:p-10 text-center flex-shrink-0 w-72 sm:w-80">
                <div className="mb-4 flex justify-center">
                  <img 
                    src="/avatar1.png" 
                    alt="Julia"
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white text-xl sm:text-2xl font-medium mx-auto">J</div>';
                    }}
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Julia</h3>
                <p className="text-sm text-gray-600 mb-3">Co-host in Mexico City, Mexico</p>
                <div className="space-y-1">
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-700">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium">4.89</span>
                  </div>
                  <p className="text-xs text-gray-500">5 years hosting</p>
                </div>
              </div>

              {/* Co-host 2 */}
              <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-8 sm:p-10 text-center flex-shrink-0 w-72 sm:w-80">
                <div className="mb-4 flex justify-center">
                  <img 
                    src="/avatar2.png" 
                    alt="Gabrielle"
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white text-xl sm:text-2xl font-medium mx-auto">G</div>';
                    }}
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Gabrielle</h3>
                <p className="text-sm text-gray-600 mb-3">Co-host in North Charleston, USA</p>
                <div className="space-y-1">
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-700">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium">4.98</span>
                  </div>
                  <p className="text-xs text-gray-500">3 years hosting</p>
                </div>
              </div>

              {/* Co-host 3 */}
              <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-8 sm:p-10 text-center flex-shrink-0 w-72 sm:w-80">
                <div className="mb-4 flex justify-center">
                  <img 
                    src="/avatar3.png" 
                    alt="Mariam"
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white text-xl sm:text-2xl font-medium mx-auto">M</div>';
                    }}
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Mariam</h3>
                <p className="text-sm text-gray-600 mb-3">Co-host in Los Angeles, USA</p>
                <div className="space-y-1">
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-700">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium">4.94</span>
                  </div>
                  <p className="text-xs text-gray-500">5 years hosting</p>
                </div>
              </div>

              {/* Co-host 4 */}
              <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-8 sm:p-10 text-center flex-shrink-0 w-72 sm:w-80">
                <div className="mb-4 flex justify-center">
                  <img 
                    src="/avatar4.png" 
                    alt="Ethan"
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white text-xl sm:text-2xl font-medium mx-auto">E</div>';
                    }}
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Ethan</h3>
                <p className="text-sm text-gray-600 mb-3">Co-host in Temple, USA</p>
                <div className="space-y-1">
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-700">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium">4.98</span>
                  </div>
                  <p className="text-xs text-gray-500">4 years hosting</p>
                </div>
              </div>

              {/* Co-host 5 */}
              <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-8 sm:p-10 text-center flex-shrink-0 w-72 sm:w-80">
                <div className="mb-4 flex justify-center">
                  <img 
                    src="/avatar5.png" 
                    alt="Anna"
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white text-xl sm:text-2xl font-medium mx-auto">A</div>';
                    }}
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Anna</h3>
                <p className="text-sm text-gray-600 mb-3">Co-host in Toronto, Canada</p>
                <div className="space-y-1">
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-700">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium">4.95</span>
                  </div>
                  <p className="text-xs text-gray-500">9 years hosting</p>
                </div>
              </div>
              </div>
            </div>

            {/* Find a co-host Button */}
            <div className="text-center max-w-7xl mx-auto px-4">
              <button 
                onClick={() => setIsCoHostModalOpen(true)}
                className="group bg-emerald-700 hover:bg-emerald-800 text-white px-8 sm:px-10 lg:px-12 py-4 sm:py-5 rounded-lg text-base sm:text-lg font-medium shadow-lg shadow-emerald-200/50 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-300/50 flex items-center justify-center mx-auto"
              >
                <span className="flex items-center justify-center">
                  Find a co-host
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Co-host Request Modal */}
      <CoHostRequestModal 
        isOpen={isCoHostModalOpen} 
        onClose={() => setIsCoHostModalOpen(false)} 
      />
    </>
  );
};

export default Hero; 